import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { HelperStateService } from '../../../services/helperState.service';
import { VwPlacement } from '../floating-position.util';
import { VwTooltipComponent } from './vw-tooltip.component';

export type VwTooltipTrigger = 'hover' | 'click' | 'auto' | 'none';

// Attaches a rich, dismissible tooltip to any element without needing
// @angular/cdk's overlay package — the tooltip component is created with
// ComponentFactoryResolver and its root node appended straight to <body>,
// matching how the rest of this codebase does dynamic UI (see the tour
// overlay for the same pattern).
@Directive({ selector: '[vwTooltip]' })
export class VwTooltipDirective implements OnInit, OnDestroy {
  // A stable key identifying this tooltip for "seen"/"don't show again"
  // persistence, e.g. "tooltip.dashboard.property-filter". Optional — omit
  // it for a plain, non-persisted hover tip.
  @Input('vwTooltip') tooltipKey: string;
  @Input() vwTooltipTitle: string;
  @Input() vwTooltipBody: string;
  @Input() vwTooltipImage: string;
  @Input() vwTooltipLinkText: string;
  @Input() vwTooltipLinkUrl: string;
  @Input() vwTooltipPlacement: VwPlacement = 'auto';
  @Input() vwTooltipTrigger: VwTooltipTrigger = 'hover';
  @Input() vwTooltipDismissible = true;

  private componentRef: ComponentRef<VwTooltipComponent> | null = null;
  private docClickListener: (ev: MouseEvent) => void;
  private loadedSub: Subscription;
  private hideTimer: any;

  constructor(
    private el: ElementRef<HTMLElement>,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    private helperState: HelperStateService
  ) {}

  ngOnInit(): void {
    if (this.vwTooltipTrigger === 'auto') {
      this.loadedSub = this.helperState.loaded$.subscribe(loaded => {
        if (loaded && this.tooltipKey && !this.helperState.isDismissed(this.tooltipKey)) {
          setTimeout(() => this.show(), 500);
        }
      });
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.vwTooltipTrigger === 'hover') {
      clearTimeout(this.hideTimer);
      this.show();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.vwTooltipTrigger === 'hover') {
      this.hideTimer = setTimeout(() => this.hide(), 150);
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (this.vwTooltipTrigger === 'hover') {
      this.show();
    }
  }

  @HostListener('blur')
  onBlur(): void {
    if (this.vwTooltipTrigger === 'hover') {
      this.hide();
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.vwTooltipTrigger === 'click') {
      event.stopPropagation();
      this.componentRef ? this.hide() : this.show();
    }
  }

  private show(): void {
    if (this.componentRef) {
      return;
    }
    const factory = this.resolver.resolveComponentFactory(VwTooltipComponent);
    this.componentRef = factory.create(this.injector);
    this.appRef.attachView(this.componentRef.hostView);
    const domRoot = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domRoot);

    const inst = this.componentRef.instance;
    inst.anchor = this.el.nativeElement;
    inst.title = this.vwTooltipTitle;
    inst.body = this.vwTooltipBody;
    inst.image = this.vwTooltipImage;
    inst.linkText = this.vwTooltipLinkText;
    inst.linkUrl = this.vwTooltipLinkUrl;
    inst.placement = this.vwTooltipPlacement;
    inst.dismissible = this.vwTooltipDismissible;
    inst.dismiss.subscribe(() => this.hide());
    inst.dontShowAgain.subscribe(() => {
      if (this.tooltipKey) {
        this.helperState.markSeen(this.tooltipKey, true).subscribe();
      }
      this.hide();
    });

    setTimeout(() => inst.reposition(), 0);

    if (this.vwTooltipTrigger === 'click') {
      this.docClickListener = () => this.hide();
      setTimeout(() => document.addEventListener('click', this.docClickListener), 0);
    }
  }

  private hide(): void {
    if (!this.componentRef) {
      return;
    }
    this.appRef.detachView(this.componentRef.hostView);
    this.componentRef.destroy();
    this.componentRef = null;
    if (this.docClickListener) {
      document.removeEventListener('click', this.docClickListener);
      this.docClickListener = null;
    }
  }

  ngOnDestroy(): void {
    this.hide();
    if (this.loadedSub) {
      this.loadedSub.unsubscribe();
    }
    clearTimeout(this.hideTimer);
  }
}
